import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  ArchiveBoxIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

import { 
  useRelease, 
  useReleaseLifecycle 
} from '../hooks/useRelease';
import { useRoles } from '../auth/useRoles';
import { LoadingSpinner, ConfirmDialog } from '../components/common';
import { ReleaseFlowViewer } from '../components/ReleaseFlow/ReleaseFlowViewer';
import { ReleaseNotesSection } from '../components/ReleaseNotesSection';
import AiReleaseNotesSummarySection from '../components/AiReleaseNotesSummarySection';
import type { 
  ReleaseDto, 
  ReleaseActionDto 
} from '../types/release';
import type { ReleaseFlowDto } from '../types/releaseFlow';

export const ReleaseViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useRoles();
  
  const [actionConfirm, setActionConfirm] = useState<{
    isOpen: boolean;
    action?: 'activate' | 'archive' | 'ready';
    release?: ReleaseDto;
  }>({ isOpen: false });

  // Fetch release data
  const { data: release, isLoading, error } = useRelease(id);
  
  // Parse the release flow snapshot instead of fetching live data
  const releaseFlow = React.useMemo(() => {
    if (!release?.configurationSnapshot) return null;
    
    try {
      return JSON.parse(release.configurationSnapshot) as ReleaseFlowDto;
    } catch (error) {
      console.error('Failed to parse release flow snapshot:', error);
      return null;
    }
  }, [release?.configurationSnapshot]);

  const { activateRelease, archiveRelease, markReleaseReady, isUpdating } = useReleaseLifecycle();

  const handleBack = () => {
    navigate('/releases');
  };

  const handleEdit = () => {
    navigate(`/releases/${id}/edit`);
  };

  const handleLifecycleAction = (action: 'activate' | 'archive' | 'ready') => {
    if (!release) return;
    setActionConfirm({ isOpen: true, action, release });
  };

  const confirmAction = async () => {
    if (!actionConfirm.action || !actionConfirm.release) return;

    const actionData: ReleaseActionDto = {
      actionBy: 'current-user' // TODO: Get from auth context
    };

    try {
      switch (actionConfirm.action) {
        case 'activate':
          await activateRelease(actionConfirm.release.id, actionData.actionBy);
          toast.success('Release activated successfully');
          break;
        case 'archive':
          await archiveRelease(actionConfirm.release.id, actionData.actionBy);
          toast.success('Release archived successfully');
          break;
        case 'ready':
          await markReleaseReady(actionConfirm.release.id, actionData.actionBy);
          toast.success('Release marked as ready');
          break;
      }
    } catch (error) {
      console.error(`Failed to ${actionConfirm.action} release:`, error);
      toast.error(`Failed to ${actionConfirm.action} release`);
    }

    setActionConfirm({ isOpen: false });
  };

  const getActionButtonConfig = () => {
    if (!release) return null;

    switch (release.lifecycleState) {
      case 'Draft':
        return release.canActivate 
          ? { 
              action: 'ready' as const, 
              label: 'Mark Ready', 
              icon: CheckCircleIcon, 
              color: 'green' 
            }
          : null;
      case 'Ready':
        return { 
          action: 'activate' as const, 
          label: 'Activate', 
          icon: PlayIcon, 
          color: 'blue' 
        };
      case 'Active':
        return { 
          action: 'archive' as const, 
          label: 'Archive', 
          icon: ArchiveBoxIcon, 
          color: 'purple' 
        };
      default:
        return null;
    }
  };

  const actionButton = getActionButtonConfig();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="mt-4 text-2xl font-semibold text-gray-900">Error Loading Release</h1>
            <p className="mt-2 text-gray-600">
              There was a problem loading the release. Please try again later.
            </p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Releases
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Release Not Found</h1>
            <p className="mt-2 text-gray-600">
              The release you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <button
              type="button"
              onClick={handleBack}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Back to Releases
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Release: {release.name}
                </h1>
                <p className="text-sm text-gray-600">
                  {release.description || 'No description provided'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Lifecycle State Badge */}
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                release.lifecycleState === 'Draft' 
                  ? 'bg-gray-100 text-gray-800'
                  : release.lifecycleState === 'Ready'
                  ? 'bg-yellow-100 text-yellow-800'
                  : release.lifecycleState === 'Active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-purple-100 text-purple-800'
              }`}>
                {release.lifecycleState}
              </span>

              {/* Action Button */}
              {canWrite && actionButton && (
                <button
                  type="button"
                  onClick={() => handleLifecycleAction(actionButton.action)}
                  disabled={isUpdating}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    actionButton.color === 'green' 
                      ? 'bg-green-600 hover:bg-green-700'
                      : actionButton.color === 'blue'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  } disabled:opacity-50`}
                >
                  <actionButton.icon className="mr-2 h-4 w-4" />
                  {actionButton.label}
                </button>
              )}

              {/* Edit Button */}
              {canWrite && release.isEditable && (
                <button
                  type="button"
                  onClick={handleEdit}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PencilIcon className="mr-2 h-4 w-4" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Release Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Release Information</h2>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Release Flow</label>
                <p className="mt-1 text-sm text-gray-900">
                  {release.releaseFlowName || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Version</label>
                <p className="mt-1 text-sm text-gray-900">
                  {release.version || 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm text-gray-900">
                  {release.status}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created By</label>
                <p className="mt-1 text-sm text-gray-900">
                  {release.createdBy}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created On</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(release.createdOn).toLocaleString()}
                </p>
              </div>
              {release.activatedOn && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Activated On</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(release.activatedOn).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Release Flow Visualization */}
        {releaseFlow && (
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Release Flow</h2>
              <p className="mt-1 text-sm text-gray-600">
                Interactive flow diagram showing the release pipeline. Click on pipeline nodes to select pipeline runs.
              </p>
            </div>
            <div className="h-96 border">
              <ReleaseFlowViewer
                releaseFlow={releaseFlow}
                releaseId={release.id}
                releaseNodePipelineRuns={release.pipelineRuns || []}
              />
            </div>
          </div>
        )}

        {/* Pipeline Runs Section */}
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Pipeline Runs</h2>
            <p className="mt-1 text-sm text-gray-600">
              Associate specific pipeline runs with nodes in this release.
            </p>
          </div>
          <div className="px-6 py-4">
            <div className="text-center py-8 text-gray-500">
              <p>Pipeline run association functionality coming soon...</p>
              <p className="mt-2 text-sm">
                Click on nodes in the release flow above to associate pipeline runs.
              </p>
            </div>
          </div>
        </div>

        {/* Release Notes Section */}
        <ReleaseNotesSection releaseId={release.id} />

        {/* AI Release Notes Summary Section */}
        {['Ready', 'Active', 'Archived'].includes(release.lifecycleState) && (
          <AiReleaseNotesSummarySection releaseId={release.id} />
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={actionConfirm.isOpen}
        onClose={() => setActionConfirm({ isOpen: false })}
        onConfirm={confirmAction}
        title={`${actionConfirm.action === 'activate' ? 'Activate' : 
                actionConfirm.action === 'archive' ? 'Archive' : 'Mark Ready'} Release`}
        message={`Are you sure you want to ${actionConfirm.action} the release "${actionConfirm.release?.name}"?`}
        confirmLabel={actionConfirm.action === 'activate' ? 'Activate' : 
                    actionConfirm.action === 'archive' ? 'Archive' : 'Mark Ready'}
        cancelLabel="Cancel"
        isDestructive={actionConfirm.action === 'archive'}
      />
    </div>
  );
};

export default ReleaseViewPage;